import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import {
  useLazyGetAdminAgreementExecutionQuery,
  useLazyGetAdminAgreementReviewQuery,
  useLazyGetAdminAgreementSetupQuery,
  useLazyGetAgreementExecutionQuery,
  useLazyGetAgreementReviewQuery,
  useLazyGetAgreementSetupQuery,
  usePrepareAgreementMutation,
  useSignAgreementMutation,
} from '../../store/api';
import type {
  AgreementExecutionResponse,
  AgreementPrepareRequest,
  AgreementReviewResponse,
  AgreementSetupResponse,
  AgreementSignRequest,
} from '../../types';
import type { IntakeExperienceContext } from '../shell/IntakeExperience';

import { agreementErrorMessage, agreementView, capturedSignature } from './agreementApiMapping';
import { AgreementPreparationForm } from './AgreementPreparationForm';
import { AgreementsSetup } from './AgreementsSetup';

export interface IntakeAgreementExperienceProps {
  context: IntakeExperienceContext;
  enabled: boolean;
  /** Current authenticated actor and practice; prevents stale in-memory state across scope changes. */
  scopeKey: string;
  onContinueToIntake: () => void;
}

export function IntakeAgreementExperience(props: IntakeAgreementExperienceProps) {
  return <AgreementSession key={props.scopeKey} {...props} />;
}

function AgreementSession({
  context,
  enabled,
  onContinueToIntake,
}: IntakeAgreementExperienceProps) {
  const isAdmin = context.kind === 'admin';
  const organizationId = isAdmin ? context.organizationId : '';
  const { intakeHub } = useEventTracking();
  const [getSetup] = useLazyGetAgreementSetupQuery();
  const [getAdminSetup] = useLazyGetAdminAgreementSetupQuery();
  const [getReview] = useLazyGetAgreementReviewQuery();
  const [getAdminReview] = useLazyGetAdminAgreementReviewQuery();
  const [getExecution] = useLazyGetAgreementExecutionQuery();
  const [getAdminExecution] = useLazyGetAdminAgreementExecutionQuery();
  const [prepare] = usePrepareAgreementMutation();
  const [sign] = useSignAgreementMutation();
  const [setup, setSetup] = useState<AgreementSetupResponse>();
  const [review, setReview] = useState<AgreementReviewResponse>();
  const [executions, setExecutions] = useState<AgreementExecutionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [awaitingSavedReload, setAwaitingSavedReload] = useState(false);
  const [error, setError] = useState<string>();
  const preparedId = useRef<string | undefined>(undefined);
  const generation = useRef(0);
  const alive = useRef(true);
  const prepareAttempt = useRef<{ binding: string; request: AgreementPrepareRequest } | undefined>(
    undefined,
  );
  const signAttempts = useRef(
    new Map<string, { binding: string; request: AgreementSignRequest }>(),
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current += 1;
    };
  }, []);

  const readSaved = useCallback(
    async (nextReview: AgreementReviewResponse) => {
      const saved = await Promise.all(
        nextReview.executions.map(execution =>
          isAdmin
            ? getAdminExecution(
                { organizationId, executionId: execution.execution_id },
                false,
              ).unwrap()
            : getExecution({ executionId: execution.execution_id }, false).unwrap(),
        ),
      );
      // Never merge a retained signature from another package or tenant into the view.
      if (
        saved.some(
          item =>
            item.envelope.package_sha256 !== nextReview.package_sha256 ||
            item.envelope.package.preparation_id !== nextReview.package.preparation_id ||
            item.envelope.package.organization_id !== nextReview.package.organization_id,
        )
      )
        throw new Error('Saved agreement does not match');
      return saved;
    },
    [getAdminExecution, getExecution, isAdmin, organizationId],
  );

  const reload = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setLoading(true);
    setError(undefined);
    try {
      const nextSetup = isAdmin
        ? await getAdminSetup({ organizationId }, false).unwrap()
        : await getSetup(undefined, false).unwrap();
      const id = nextSetup.committed_preparation_id ?? preparedId.current;
      const nextReview = id
        ? isAdmin
          ? await getAdminReview({ organizationId, preparationId: id }, false).unwrap()
          : await getReview({ preparationId: id }, false).unwrap()
        : undefined;
      const saved = nextReview ? await readSaved(nextReview) : [];
      if (!alive.current || requestGeneration !== generation.current) return;
      preparedId.current = id;
      setSetup(nextSetup);
      setReview(nextReview);
      setExecutions(saved);
      setAwaitingSavedReload(false);
      if (
        nextSetup.state === 'unsigned' &&
        saved.length === 0 &&
        (!nextReview || nextReview.state === 'prepared')
      )
        signAttempts.current.clear();
    } catch (cause) {
      if (alive.current && requestGeneration === generation.current)
        setError(agreementErrorMessage(cause));
      throw cause;
    } finally {
      if (alive.current && requestGeneration === generation.current) setLoading(false);
    }
  }, [getAdminReview, getAdminSetup, getReview, getSetup, isAdmin, organizationId, readSaved]);

  useEffect(() => {
    if (enabled) void reload().catch(() => undefined);
    else setLoading(false);
  }, [enabled, reload]);

  const preparePackage = async (values: Omit<AgreementPrepareRequest, 'request_id'>) => {
    if (isAdmin || !enabled || !setup?.can_sign || !setup.v3_enabled || preparing) return;
    const binding = JSON.stringify(values);
    if (prepareAttempt.current?.binding !== binding)
      prepareAttempt.current = { binding, request: { ...values, request_id: crypto.randomUUID() } };
    setPreparing(true);
    setError(undefined);
    intakeHub.agreementActionClicked({ action: 'prepare' });
    try {
      const result = await prepare({ prepareRequest: prepareAttempt.current.request }).unwrap();
      const saved = await readSaved(result);
      if (alive.current) {
        preparedId.current = result.package.preparation_id;
        setReview(result);
        setExecutions(saved);
      }
    } catch (cause) {
      if (alive.current) setError(agreementErrorMessage(cause));
    } finally {
      if (alive.current) setPreparing(false);
    }
  };

  const signDocument = async (documentId: string, capture: string) => {
    if (isAdmin || !enabled || !setup?.can_sign || !setup.v3_enabled || !review?.can_sign)
      throw new Error('Signing not available');
    const document = review.package.documents.find(item => item.document_key === documentId);
    const documentDigest = document && review.document_digests[document.document_key];
    if (!document || !documentDigest) throw new Error('Agreement is not prepared');
    const signature = capturedSignature(capture);
    const binding = JSON.stringify([
      review.package_sha256,
      documentDigest,
      document.consent.version,
      signature,
    ]);
    let attempt = signAttempts.current.get(documentId);
    if (attempt?.binding !== binding) {
      attempt = {
        binding,
        request: {
          request_id: crypto.randomUUID(),
          preparation_id: review.package.preparation_id,
          package_sha256: review.package_sha256,
          document_sha256: documentDigest,
          consent_version: document.consent.version,
          accepted: true,
          authority_confirmed: true,
          signature,
        },
      };
      signAttempts.current.set(documentId, attempt);
    }
    setSigning(true);
    intakeHub.agreementActionClicked({ action: 'sign', document_key: document.document_key });
    setError(undefined);
    let saved = false;
    try {
      await sign({
        preparationId: review.package.preparation_id,
        documentKey: document.document_key,
        signRequest: attempt.request,
      }).unwrap();
      saved = true;
      // The returned execution is persisted, but the UI only renders retained envelopes.
      await reload();
    } catch (cause) {
      if (alive.current) {
        setAwaitingSavedReload(saved);
        setError(
          saved
            ? 'Your signature was saved, but we couldn’t reload the signed agreement. Reload saved agreements to continue.'
            : agreementErrorMessage(cause),
        );
      }
      throw new Error('Agreement save not confirmed');
    } finally {
      if (alive.current) setSigning(false);
    }
  };

  if (!enabled)
    return (
      <Alert severity="info">
        Agreements are not enabled for this practice. Your existing intake remains available.
      </Alert>
    );
  if (loading)
    return (
      <Stack sx={{ p: 4, alignItems: 'center' }} spacing={2}>
        <CircularProgress size={28} />
        <Typography>Loading your agreements…</Typography>
      </Stack>
    );
  const view = review ? agreementView(review, executions) : undefined;
  return (
    <Box sx={{ minHeight: '100%', bgcolor: '#f7f7fb' }}>
      <Stack spacing={2} sx={{ p: 2 }}>
        {review?.package.documents.some(
          document => 'synthetic' in document.approval && document.approval.synthetic,
        ) && (
          <Alert severity="info">
            Test agreement. Synthetic terms and signatures are for verification only.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button
            disabled={preparing || signing}
            onClick={() => {
              if (!isAdmin) intakeHub.agreementActionClicked({ action: 'reload' });
              void reload().catch(() => undefined);
            }}
          >
            Reload saved agreements
          </Button>
        </Box>
        {setup && !setup.v3_enabled && (
          <Alert severity="info">
            Signing is disabled for this practice. Any saved agreement remains available to read.
          </Alert>
        )}
        {setup && !view && (isAdmin || !setup.can_sign) && (
          <Alert severity="info">
            No signed dashboard agreement is available here. The practice administrator signs from
            their own account. Existing agreements and intake remain unchanged.
          </Alert>
        )}
      </Stack>
      {!view && !isAdmin && setup?.v3_enabled && setup.can_sign && setup.options && (
        <AgreementPreparationForm
          key={setup.options.config_revision}
          packages={setup.options.packages}
          configRevision={setup.options.config_revision}
          pending={preparing}
          onPrepare={values => void preparePackage(values)}
        />
      )}
      {view && review && !awaitingSavedReload && (
        <>
          {!isAdmin && review.state === 'prepared' && setup?.v3_enabled && (
            <Box sx={{ px: 2 }}>
              <Button
                disabled={signing || signAttempts.current.size > 0}
                onClick={() => {
                  preparedId.current = undefined;
                  setReview(undefined);
                  signAttempts.current.clear();
                }}
              >
                Change prepared services
              </Button>
            </Box>
          )}
          <AgreementsSetup
            preparationKey={`${review.package.organization_id}:${review.package.preparation_id}:${review.package_sha256}`}
            practiceName={isAdmin ? 'Selected practice' : 'Your practice'}
            context={context}
            showStageRail={false}
            packages={[view]}
            selectedPackageId={view.id}
            canSign={!isAdmin && Boolean(setup?.can_sign && setup.v3_enabled && review.can_sign)}
            initialSection="agreements"
            onSelectPackage={() => undefined}
            onSign={signDocument}
            onContinueToIntake={() => {
              if (!isAdmin) intakeHub.agreementActionClicked({ action: 'continue' });
              onContinueToIntake();
            }}
          />
        </>
      )}
    </Box>
  );
}
